import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { DemoDataService } from '@core/services/demo-data.service';
import { OKTA_LOGIN_IN_PROGRESS_SESSION_KEY } from '@core/okta-integration/okta-session-keys';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, UserClaims } from '@okta/okta-auth-js';
import {
  BehaviorSubject,
  catchError,
  filter,
  from,
  map,
  merge,
  Observable,
  of,
  throwError,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OktaIntegrationService {
  private _oktaAuth = inject(OKTA_AUTH);
  private _router = inject(Router);
  private _oktaAuthStateService = inject(OktaAuthStateService);
  private _demoDataService = inject(DemoDataService);
  private _analyticsService = inject(AnalyticsService);
  private _demoUserLoggedIn = false;
  private _wasAuthenticated = false;
  private _demoAuthState$ = new BehaviorSubject<boolean>(false);
  private OKTA_ORIGINAL_URI_KEY = 'okta_original_uri';
  private static readonly LOGIN_IN_PROGRESS_KEY =
    OKTA_LOGIN_IN_PROGRESS_SESSION_KEY;

  constructor() {
    this.getIsAuthenticated().subscribe(isAuthenticated => {
      if (isAuthenticated && !this._wasAuthenticated) {
        if (this.consumeLoginInProgressFlag()) {
          this._analyticsService.trackLogin('okta');
        }
      }
      this._wasAuthenticated = isAuthenticated;
    });
  }

  private markLoginInProgress(): void {
    try {
      sessionStorage.setItem(OktaIntegrationService.LOGIN_IN_PROGRESS_KEY, '1');
    } catch {
      console.warn('sessionStorage is not available');
    }
  }

  private consumeLoginInProgressFlag(): boolean {
    try {
      const wasInProgress =
        sessionStorage.getItem(OktaIntegrationService.LOGIN_IN_PROGRESS_KEY) ===
        '1';
      sessionStorage.removeItem(OktaIntegrationService.LOGIN_IN_PROGRESS_KEY);
      return wasInProgress;
    } catch {
      return false;
    }
  }

  public getIsAuthenticated(): Observable<boolean> {
    return merge(
      this._demoAuthState$.pipe(
        filter(() => this._demoDataService.isDemoActive())
      ),
      this._oktaAuthStateService.authState$.pipe(
        filter(() => !this._demoDataService.isDemoActive()),
        filter((s: AuthState) => !!s),
        map((s: AuthState) => s.isAuthenticated ?? false)
      )
    );
  }

  public signInWithRedirect(
    origin: string,
    navigateTo: string
  ): Observable<void> {
    this.markLoginInProgress();

    if (this._demoDataService.isDemoActive()) {
      this._demoUserLoggedIn = true;
      this._demoAuthState$.next(true);
      return of();
    }

    return from(
      this._oktaAuth.signInWithRedirect({ originalUri: origin })
    ).pipe(
      map(() => {
        void this._router.navigate([navigateTo], {
          queryParamsHandling: 'preserve',
        });
      }),
      catchError((error: unknown) => {
        console.error('Okta sign in error:', error);
        return throwError(() => error);
      })
    );
  }

  public signOut(): Observable<void> {
    this._analyticsService.trackLogout('okta');

    if (this._demoDataService.isDemoActive()) {
      this._demoUserLoggedIn = false;
      this._demoAuthState$.next(false);
      return of();
    }

    return from(this._oktaAuth.signOut()).pipe(
      map(() => {
        const checkoutUrl = location.pathname;
        const urlParameters = new URLSearchParams(globalThis.location.search);
        const token = urlParameters.get('token');
        const checkoutUrlData = JSON.stringify({
          path: checkoutUrl,
          token,
          timestamp: Date.now(),
        });
        localStorage.setItem('checkout-url', checkoutUrlData);
      }),
      catchError((error: unknown) => {
        console.error('Okta sign out error:', error);
        return throwError(() => error);
      })
    );
  }

  public async getUserInfo(): Promise<UserClaims> {
    if (this._demoDataService.isDemoActive() && this._demoUserLoggedIn) {
      return {
        sub: 'demo-user-123',
        email: 'demo@example.com',
        email_verified: true,
        name: 'Demo User',
        given_name: 'Demo',
        family_name: 'User',
        preferred_username: 'demo@example.com',
        accountCredit: 100_000,
      } as UserClaims;
    }
    return await this._oktaAuth.getUser();
  }

  public getAccessToken(): string {
    if (this._demoDataService.isDemoActive() && this._demoUserLoggedIn) {
      return 'demo-access-token';
    }
    return this._oktaAuth.getAccessToken()!;
  }
}
